<template>
  <div
    ref="wrapRef"
    class="datepicker-wrap"
    :class="{ 'datepicker-wrap--disabled': isDisabled }"
    @keydown.enter.prevent
  >
  <Datepicker
    ref="dpRef"
    :disabled="isDisabled"
    v-model="safeModel"
    :placeholder="placeholder"
    :auto-apply="!confirm"
    :min-date="minDate"
    :max-date="maxDate"
    :hide-input-icon="iconHide"
    :locale="locale"
    :enable-time-picker="enableTime"
    :format="formatProp"
    :range="range"
    :inline="inline"
    :ui="{ input: 'form-control' }"
    :multi-calendars="multi"
    :text-input="textInputConfig"
    :teleport="teleport"
    :auto-position="false"
    :alt-position="calendarMenuPosition"
    :action-row="{
      showNow: false,
      showSelect: false,
      showCancel: false,
      showPreview: false,
      keepActionRow: true,
    }"
  >
    <template #action-buttons>
      <div class="dp__action_buttons dp__action_buttons--custom">
        <button
          type="button"
          class="dp__action_button"
          @click="selectToday"
        >
          {{ t('today') }}
        </button>
        <button
          type="button"
          class="dp__action_button dp__action_cancel"
          @click="clearDate"
        >
          {{ t('clear') }}
        </button>
        <button
          v-if="confirm"
          type="button"
          class="dp__action_button dp__action_select"
          @click="applyConfirm"
        >
          {{ t('select') }}
        </button>
      </div>
    </template>
    <template #dp-input="{ value, onInput, onBlur, onFocus, onEnter, onTab, onKeypress }">
      <div v-if="iconHide" class="dp__input_wrap dp__input_wrap--single">
        <input
          :value="inputText(value)"
          :placeholder="placeholder"
          class="form-control dp__input"
          :class="{ 'is-invalid': props.error }"
          :disabled="isDisabled"
          :readonly="isDisabled"
          :tabindex="isDisabled ? -1 : undefined"
          data-test-id="dp-input"
          inputmode="text"
          autocomplete="off"
          :maxlength="inputMaxLength"
          @input="(e) => handleInput(e, onInput)"
          @paste="(e) => handlePaste(e, onInput)"
          @blur="(e) => handleBlur(e, onBlur)"
          @focus="(e) => handleFocus(e, onFocus)"
          @keydown.enter="onEnter"
          @keydown.tab="onTab"
          @keypress="onKeypress"
        />
      </div>
      <div v-else class="input-group w-100">
        <span
          class="input-group-text dp__input_icon_decorative"
          :class="{ 'dp__input_icon_decorative--clickable': canOpenMenu }"
          role="button"
          :tabindex="canOpenMenu && !isDisabled ? 0 : -1"
          @click.stop="onIconClick"
          @keydown.enter.prevent="onIconClick"
          @keydown.space.prevent="onIconClick"
        >
          <i class="bi bi-calendar"></i>
        </span>
        <input
          :value="inputText(value)"
          :placeholder="placeholder"
          class="form-control dp__input"
          :class="{ 'is-invalid': props.error }"
          :disabled="isDisabled"
          :readonly="isDisabled"
          :tabindex="isDisabled ? -1 : undefined"
          data-test-id="dp-input"
          inputmode="text"
          autocomplete="off"
          :maxlength="inputMaxLength"
          @input="(e) => handleInput(e, onInput)"
          @paste="(e) => handlePaste(e, onInput)"
          @blur="(e) => handleBlur(e, onBlur)"
          @focus="(e) => handleFocus(e, onFocus)"
          @keydown.enter="onEnter"
          @keydown.tab="onTab"
          @keypress="onKeypress"
        />
      </div>
    </template>
  </Datepicker>
  </div>
</template>
<script setup>
import { computed, inject, nextTick, onBeforeUnmount, ref, shallowRef, toValue, watch } from 'vue'
import { useI18n } from 'vue-i18n'
// TODO: какие-то проблемы с импортом, поэтому используется альтернативный импорт
import * as _dp from '@vuepic/vue-datepicker'
import { parseDateInput, formatDateForInput, formatQuarterFromDate, autoInsertDots } from '@/lib/dateInput.js'

const Datepicker = _dp.default || _dp

const { t, locale } = useI18n()

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  maxDate: {
    type: [String, Date],
    default: null,
  },
  minDate: {
    type: [String, Date],
    default: null,
  },
  iconHide: {
    type: Boolean,
  },
  placeholder: String,
  enableTime: Boolean,
  range: Boolean,
  confirm: Boolean,
  inline: Boolean,
  multi: Boolean,
  teleport: {
    type: Boolean,
    default: true,
  },
  /**
   * Открывать ли календарь при фокусе/клике (vue-datepicker textInput.openMenu).
   * false — только ручной ввод без попапа.
   */
  openMenu: {
    type: [Boolean, String],
    default: undefined,
  },
  allowQuarterFormat: {
    type: Boolean,
    default: true,
  },
  /** Внешняя ошибка формы: текст показывает родительский layout, control только включает invalid-состояние. */
  error: {
    type: String,
    default: '',
  },
})

const model = defineModel()
const dpRef = ref(null)
const wrapRef = ref(null)
const pinId = `dp-pin-${Math.random().toString(36).slice(2)}`
const calendarPinBus = (globalThis.__ltmsDpPin ||= { id: null })
let pinFrame = 0
let pinMisses = 0

/** Поле, к которому клеится календарь: вся строка с иконкой, иначе сам input. */
function fieldAnchor() {
  const wrap = wrapRef.value
  if (!wrap) {
    return null
  }
  return wrap.querySelector('.input-group') || wrap.querySelector('input.dp__input') || wrap
}

/**
 * Ставит меню ровно под это поле. Координаты только из getBoundingClientRect поля,
 * без вычитания прошлой ошибки.
 *
 * @returns {'ok' | 'wait' | 'stop'} меню на месте, ещё не в DOM, или его ведёт другое поле
 */
function pinCalendar() {
  if (calendarPinBus.id !== pinId) {
    return 'stop'
  }
  const anchor = fieldAnchor()
  const shell = document.querySelector('.dp__outer_menu_wrap.dp--menu-wrapper')
  const menu = shell?.querySelector(':scope > .dp__menu')
  if (!anchor?.isConnected || !shell || !menu) {
    return 'wait'
  }
  const rect = anchor.getBoundingClientRect()
  if (!rect.width) {
    return 'wait'
  }
  const width = menu.offsetWidth || 0
  const view = window.visualViewport
  const viewLeft = view?.offsetLeft || 0
  const viewWidth = view?.width || document.documentElement.clientWidth
  const margin = 8
  let left = rect.left
  if (width && left + width > viewLeft + viewWidth - margin) {
    left = viewLeft + viewWidth - margin - width
  }
  if (left < viewLeft + margin) {
    left = viewLeft + margin
  }
  menu.style.setProperty('position', 'fixed', 'important')
  menu.style.setProperty('top', `${rect.bottom}px`, 'important')
  menu.style.setProperty('left', `${left}px`, 'important')
  menu.style.setProperty('right', 'auto', 'important')
  menu.style.setProperty('bottom', 'auto', 'important')
  menu.style.setProperty('transform', 'none', 'important')
  menu.style.setProperty('margin', '0', 'important')
  return 'ok'
}

function trackCalendar() {
  const state = pinCalendar()
  if (state === 'stop') {
    pinFrame = 0
    return
  }
  if (state === 'wait') {
    pinMisses += 1
    if (pinMisses > 10) {
      pinFrame = 0
      return
    }
  } else {
    pinMisses = 0
  }
  pinFrame = requestAnimationFrame(trackCalendar)
}

function startCalendarPin() {
  pinMisses = 0
  if (!pinFrame) {
    pinFrame = requestAnimationFrame(trackCalendar)
  }
}

onBeforeUnmount(() => {
  if (pinFrame) {
    cancelAnimationFrame(pinFrame)
    pinFrame = 0
  }
})

/**
 * Библиотека зовёт это при открытии. Дальше меню держится за поле само.
 *
 * @returns {Record<string, string>}
 */
function calendarMenuPosition() {
  calendarPinBus.id = pinId
  startCalendarPin()
  const rect = fieldAnchor()?.getBoundingClientRect()
  if (!rect?.width) {
    return { position: 'fixed', top: '0px', left: '0px', transform: 'none', margin: '0' }
  }
  return {
    position: 'fixed',
    top: `${rect.bottom}px`,
    left: `${rect.left}px`,
    transform: 'none',
    margin: '0',
  }
}
/** Текст календарной даты, который не даём библиотеке заменить на квартал. */
const pinnedDisplay = ref('')
let writingCalendarDate = false

const formEditable = inject('form:editable', () => shallowRef(true), true)
const isDisabled = computed(() => props.disabled || !toValue(formEditable))

/** dd.MM.yyyy = 10 символов; для диапазона лимит не ставим. */
const inputMaxLength = computed(() => (props.range ? undefined : 10))

/** Календарь открывается по клику, если openMenu не запрещён явно. */
const canOpenMenu = computed(() => props.openMenu !== false)

function onIconClick() {
  if (isDisabled.value || !canOpenMenu.value) {
    return
  }
  dpRef.value?.openMenu?.()
}

/** В range при одной дате или null во втором библиотека обращается к [1] → RangeError. Не вызываем format(null). */
const formatProp = computed(() => {
  return (value) => {
    try {
      if (value == null) return ''
      if (props.range && Array.isArray(value)) {
        if (value.length === 1 && value[0]) {
          const s = formatDateForInput(value[0], { allowQuarterFormat: props.allowQuarterFormat })
          return s ? `${s} - ${s}` : ''
        }
        if (value.length >= 2) {
          const s0 = value[0] ? formatDateForInput(value[0], { allowQuarterFormat: props.allowQuarterFormat }) : ''
          const s1 = value[1] ? formatDateForInput(value[1], { allowQuarterFormat: props.allowQuarterFormat }) : ''
          if (!s0) return ''
          return s1 ? `${s0} - ${s1}` : `${s0} - `
        }
        return value
          .map((d) => (d ? formatDateForInput(d, { allowQuarterFormat: props.allowQuarterFormat }) : ''))
          .filter(Boolean)
          .join(' - ')
      }
      return value ? formatDateForInput(value, { allowQuarterFormat: props.allowQuarterFormat }) : ''
    } catch {
      return ''
    }
  }
})

function inputText(libraryValue) {
  return pinnedDisplay.value || libraryValue
}

/** Полная числовая дата: 01.10.26, 01.10.2026, 011026, 01102026. */
function isCompleteNumericDate(text) {
  const s = String(text ?? '').trim()
  return /^(\d{1,2}\.\d{1,2}\.\d{2}(\d{2})?|\d{6}|\d{8})$/.test(s)
}

/**
 * Ввод дд.мм.гг остаётся календарным днём.
 * Иначе библиотека может подменить 01.10.26 на Q3 2026 (сутки UTC ещё в сентябре).
 */
function pinCalendarDate(text) {
  const trimmed = String(text ?? '').trim()
  if (!trimmed || /^q/i.test(trimmed) || props.range) {
    pinnedDisplay.value = ''
    return
  }
  if (!isCompleteNumericDate(trimmed)) return
  const parsed = parseDateInput(trimmed)
  if (!parsed || formatQuarterFromDate(parsed)) return
  const shown = formatDateForInput(parsed, { allowQuarterFormat: false })
  const local = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
  pinnedDisplay.value = shown
  writingCalendarDate = true
  safeModel.value = local
  writingCalendarDate = false
  nextTick(() => {
    if (pinnedDisplay.value !== shown) return
    const current = model.value
    const same =
      current instanceof Date &&
      current.getFullYear() === local.getFullYear() &&
      current.getMonth() === local.getMonth() &&
      current.getDate() === local.getDate() &&
      !formatQuarterFromDate(current)
    if (!same) {
      writingCalendarDate = true
      safeModel.value = local
      pinnedDisplay.value = shown
      writingCalendarDate = false
    }
  })
}

watch(model, value => {
  if (writingCalendarDate || !pinnedDisplay.value) return
  const parsed = parseDateInput(pinnedDisplay.value)
  if (!(value instanceof Date) || !parsed) {
    pinnedDisplay.value = ''
    return
  }
  const sameDay =
    value.getFullYear() === parsed.getFullYear() &&
    value.getMonth() === parsed.getMonth() &&
    value.getDate() === parsed.getDate()
  if (sameDay && !formatQuarterFromDate(value)) return
  if (formatQuarterFromDate(value)) {
    writingCalendarDate = true
    safeModel.value = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
    writingCalendarDate = false
    return
  }
  pinnedDisplay.value = ''
})

function maybeAutodotEvent(e) {
  if (props.range) return
  const el = e?.target
  if (!el || typeof el.value !== 'string') return
  const next = autoInsertDots(el.value)
  if (next && next !== el.value) el.value = next
}

function handlePaste(e, onInput) {
  if (isDisabled.value) {
    return
  }
  maybeAutodotEvent(e)
  onInput(e)
  pinCalendarDate(e?.target?.value)
}

function handleInput(e, onInput) {
  if (isDisabled.value) {
    return
  }
  maybeAutodotEvent(e)
  onInput(e)
  pinCalendarDate(e?.target?.value)
}

function handleFocus(e, onFocus) {
  if (isDisabled.value) {
    e.target.blur()
    return
  }
  onFocus(e)
}

function handleBlur(e, onBlur) {
  const text = e?.target?.value ?? ''
  onBlur()
  nextTick(() => pinCalendarDate(text))
}

/** Парсер ручного ввода: только цифры и точки, нормализация "11112026" → "11.11.2026", проверка допустимых символов. */
const textInputConfig = computed(() => {
  const cfg = {
    format: (value) => parseDateInput(value),
    enterSubmit: true,
    tabSubmit: true,
  }
  if (props.openMenu !== undefined) {
    cfg.openMenu = props.openMenu
  }
  return cfg
})

/** В range-режиме библиотека ожидает массив; при невалидном значении избегаем RangeError/TypeError (.map). */
function toValidDate(v) {
  if (v == null) return v
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v
  if (typeof v === 'string') return parseDateInput(v) ?? null
  return null
}
const safeModel = computed({
  get() {
    const v = model.value
    if (props.range) {
      if (v == null) return null
      if (!Array.isArray(v)) return null
      const a = v.map(toValidDate)
      if (a.every((x) => x == null)) return null
      const valid = a.every((x) => x == null || x instanceof Date)
      if (!valid) return null
      // Библиотека всегда ожидает два валидных Date; при одном или null во втором (ввод " - 11...") подставляем первую дату
      if (a.length === 1 || (a.length >= 2 && a[1] == null)) return [a[0], a[0]]
      if (a.length > 2) return [a[0], a[1]]
      return a
    }
    return toValidDate(v)
  },
  set(value) {
    if (props.range) {
      if (value == null) {
        model.value = null
        return
      }
      if (!Array.isArray(value)) {
        model.value = null
        return
      }
      const d0 = value[0] instanceof Date && !Number.isNaN(value[0].getTime()) ? value[0] : null
      const d1 = value[1] instanceof Date && !Number.isNaN(value[1]?.getTime()) ? value[1] : null
      if (!d0) {
        model.value = null
        return
      }
      // Храним всегда пару валидных дат; при одной дате или null во втором (ввод " - 11...") подставляем первую
      if (value.length === 1 || !d1) {
        model.value = [d0, d0]
      } else if (value.length > 2) {
        model.value = [d0, d1]
      } else {
        model.value = [d0, d1]
      }
      return
    }
    if (value == null || (value instanceof Date && !Number.isNaN(value.getTime()))) {
      model.value = value
    } else {
      model.value = null
    }
  },
})

function selectToday() {
  const now = new Date()
  safeModel.value = props.range ? [now, now] : now
  if (!props.confirm) {
    dpRef.value?.closeMenu?.()
  }
}

function clearDate() {
  pinnedDisplay.value = ''
  safeModel.value = null
  if (!props.confirm) {
    dpRef.value?.closeMenu?.()
  }
}

function applyConfirm() {
  dpRef.value?.selectDate?.()
}
</script>

<i18n>
{
  "ru": {
    "today": "Сегодня",
    "clear": "Очистить",
    "select": "Выбрать"
  },
  "en": {
    "today": "Today",
    "clear": "Clear",
    "select": "Select"
  }
}
</i18n>
